import { canvas } from './scene.js';
import { initAudio, resumeAudio } from './audio.js';
import { clearCorpses } from './corpse.js';
import { dropRemotes, setMyTeam } from './remote.js';
import { resetPrediction } from './prediction.js';
import {
  onMessage, OP, me, sendPick, sendQuit,
  MODE_TRAINING, MODE_DEATHMATCH, TEAM_T, TEAM_CT, TEAM_NONE, TEAM_SLOTS,
  MATCH_MIN_HUMANS, PHASE_WAITING, PHASE_COUNTDOWN, PHASE_LIVE, PHASE_OVER,
  LOBBY_ENTRY_LEN, SCORE_ENTRY_LEN, SCORE_NPC, SCORE_ALIVE, TEAM_NAME, TEAM_SHORT,
} from './net.js';
import { S, isTouch } from './state.js';

// ============================================================
//  Лобби и матч на клиенте: вкладки, слоты, обратный отсчёт,
//  таймер боя, счёт команд, таблица по Tab и экран итогов.
//
//  ⚠️ ГЛАВНОЕ ПРО АРХИТЕКТУРУ. Здесь нет НИ ОДНОГО игрового решения — только
//  отображение того, что сказал сервер, и три исходящих кадра (`OP_HELLO`
//  уходит из input.js, `OP_PICK` и `OP_QUIT` — отсюда). Фазу матча, состав
//  команд, счёт, момент старта и победителя считает комната; клиент даже
//  «сколько осталось секунд» не считает сам, а докручивает присланное между
//  кадрами (см. `tickClock`) — иначе таймер жил бы своей жизнью и после
//  сворачивания вкладки показывал бы не то, что на сервере.
//
//  ⚠️ Вход в игру (захват мыши) происходит ТОЛЬКО по фазе `PHASE_LIVE`.
//  Это зеркало серверного правила: до свистка `room.rs::apply_input` ввод
//  игнорирует, и забрать мышь раньше значило бы дать игроку бегать по экрану,
//  пока его тело стоит на спавне.
// ============================================================

/** Куда игрок собирается встать: 'training' | 'dm' — вкладка лобби. */
let tab = 'training';
/** Список комнат из последнего кадра `OP_LOBBY`. */
let rooms = [];
/** Мы в игре (мышь захвачена и матч идёт) — чтобы не входить дважды. */
let inGame = false;
/** Итоги показаны — чтобы не пересобирать таблицу каждые 200 мс. */
let overShown = false;

/**
 * Состояние матча — ровно то, что приехало кадром `OP_MATCH`.
 *
 * `room = 0` означает «мы в лобби»: комнат с номером 0 не бывает
 * (`lobby::TRAINING_ROOM` = 1), поэтому ноль — безопасное «нигде».
 */
export const M = {
  room: 0,
  mode: MODE_TRAINING,
  phase: PHASE_WAITING,
  team: TEAM_NONE,
  left: 0,          // секунд до конца фазы (докручивается локально)
  scoreT: 0,
  scoreCt: 0,
  humansT: 0,
  humansCt: 0,
};

/** Таблица очков из кадра `OP_SCORES`: id → { team, npc, alive, kills, deaths } */
export const scores = new Map();

/** Ники приезжают кадром JOIN — держим их здесь для таблицы и итогов. */
const nicks = new Map();

// ---- DOM ----
const el = (id) => document.getElementById(id);
const overlay = el('overlay');

/** Мы в комнате? (кадр `OP_MATCH` приходит только своим) */
export const inRoom = () => M.room !== 0;

/** Идёт ли бой прямо сейчас — зеркало `room.rs::playing`. */
export const playing = () => inRoom() && (M.mode === MODE_TRAINING || M.phase === PHASE_LIVE);

// ============================================================
//  Приём кадров
// ============================================================

onMessage((op, d, buf) => {
  if (op === OP.LOBBY) {
    // Кадр приходит ТОЛЬКО тем, кто стоит в лобби (см. `Lobby::push_lobby_all`),
    // поэтому сам факт его получения означает «мы не в комнате». Так узнаётся
    // и выселение после матча: отдельного кадра «тебя выгнали» нет намеренно —
    // это было бы второе сообщение об одном и том же.
    rooms = [];
    const count = d.getUint8(1);
    for (let i = 0; i < count; i++) {
      const o = 2 + i * LOBBY_ENTRY_LEN;
      rooms.push({
        id: d.getUint16(o, true),
        mode: d.getUint8(o + 2),
        phase: d.getUint8(o + 3),
        humans: [d.getUint8(o + 4), d.getUint8(o + 5)],
        bots: [d.getUint8(o + 6), d.getUint8(o + 7)],
        secs: d.getUint16(o + 8, true),
      });
    }
    if (inRoom()) leaveGame();
    M.room = 0;
    renderLobby();
    return;
  }

  if (op === OP.MATCH) {
    M.mode = d.getUint8(1);
    M.phase = d.getUint8(2);
    M.team = d.getUint8(3);
    M.left = d.getUint16(4, true) / 10;
    M.scoreT = d.getUint16(6, true);
    M.scoreCt = d.getUint16(8, true);
    M.humansT = d.getUint8(10);
    M.humansCt = d.getUint8(11);
    M.room = d.getUint16(12, true);
    // Своя команда нужна `remote.js`, чтобы красить союзников: импортировать
    // лобби оттуда нельзя (вышел бы цикл модулей), поэтому кладём сами.
    setMyTeam(M.team);
    applyPhase();
    return;
  }

  if (op === OP.SCORES) {
    scores.clear();
    const count = d.getUint8(1);
    for (let i = 0; i < count; i++) {
      const o = 2 + i * SCORE_ENTRY_LEN;
      const flags = d.getUint8(o + 3);
      scores.set(d.getUint16(o, true), {
        team: d.getUint8(o + 2),
        npc: (flags & SCORE_NPC) !== 0,
        alive: (flags & SCORE_ALIVE) !== 0,
        kills: d.getUint8(o + 4),
        deaths: d.getUint8(o + 5),
      });
    }
    // Свои очки в HUD — из серверной таблицы, а не из локального счётчика:
    // до матчмейкинга `S.score` увеличивался на каждый кадр `OP_HIT` с флагом
    // «убил» и ни с чем не сверялся, то есть жил отдельно от матча.
    const mine = scores.get(me.id);
    if (mine) {
      S.score = mine.kills;
      el('score').textContent = String(mine.kills);
    }
    if (S.showScores) renderScores();
    if (M.phase === PHASE_OVER) renderOver();
    return;
  }

  if (op === OP.JOIN) {
    const id = d.getUint16(1, true);
    const len = d.getUint8(3);
    nicks.set(id, new TextDecoder().decode(new Uint8Array(buf, 4, len)));
    if (S.showScores) renderScores();
    return;
  }
  if (op === OP.LEAVE) nicks.delete(d.getUint16(1, true));
});

// ============================================================
//  Фазы: вход в игру, ожидание, итоги
// ============================================================

function applyPhase() {
  if (M.phase === PHASE_OVER) {
    leaveGame();
    renderOver();
    return;
  }
  overShown = false;
  if (playing()) {
    enterGame();
  } else {
    // Слот занят, но бой не начался: показываем ожидание/отсчёт и мышь
    // не забираем — до свистка сервер ввод всё равно не применяет.
    leaveGame();
    renderWait();
  }
  updateMatchBar();
}

/**
 * Уйти в игру: захватить мышь и спрятать оверлей.
 *
 * ⚠️ Мышь может НЕ захватиться, и это не баг браузера: Pointer Lock требует
 * пользовательского жеста, а старт матча приходит кадром сервера — жеста в
 * этот момент нет вовсе (и сразу после Esc браузер держит короткую паузу).
 * Поэтому попытку делаем, но не рассчитываем на неё: экран «В БОЙ» остаётся
 * висеть, и его кнопка — тот самый жест. Получилось само — экран скроет
 * обработчик `pointerlockchange` ниже.
 */
function enterGame() {
  if (inGame) {
    updateMatchBar();
    return;
  }
  inGame = true;
  // История вводов прошлой жизни (и прошлого матча) не нужна: позиция
  // приехала новая, и переигрывать по ней старое значило бы дёрнуть игрока.
  resetPrediction();
  initAudio(); resumeAudio();
  updateMatchBar();
  if (isTouch) {
    S.started = true;
    overlay.classList.add('hidden');
    el('touch').classList.add('on');
    return;
  }
  showScreen('goScreen');
  el('goTitle').textContent = M.mode === MODE_DEATHMATCH ? 'Бой начался!' : 'Тренировка';
  grabMouse();
}

/** Попытка захватить мышь. Отказ — нормальный исход, см. `enterGame`. */
function grabMouse() {
  const p = canvas.requestPointerLock();
  // В новых браузерах это Promise; отказ ловим, чтобы он не всплыл в консоль
  // необработанным и не выглядел как настоящая ошибка.
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

// Мышь захвачена — оверлей больше не нужен; отпущена во время боя (Esc) —
// показываем паузу, но из матча НЕ выходим: тело остаётся в комнате, как
// и при Esc в любом шутере.
document.addEventListener('pointerlockchange', () => {
  if (isTouch) return;
  if (document.pointerLockElement === canvas) {
    overlay.classList.add('hidden');
  } else if (inGame) {
    showScreen('pauseScreen');
  }
});

/** Вернуть курсор и показать оверлей (ожидание, итоги или лобби). */
function leaveGame() {
  if (!inGame) return;
  inGame = false;
  S.showScores = false;
  el('scoreboard').classList.add('hidden');
  if (isTouch) {
    S.started = false;
    el('touch').classList.remove('on');
  } else if (document.pointerLockElement === canvas) {
    document.exitPointerLock();
  }
  // Красную заливку «Вы погибли» гасим здесь же: убитый за секунду до
  // свистка иначе читал бы итоги матча через неё, а respawnPlayer, который
  // её снимает, в лобби уже не случится.
  el('dead').classList.remove('on');
  // Тела и трупы прошлого матча оставлять нельзя: кадры `LEAVE` про них нам
  // уже не придут (мы сами вышли из комнаты), и они висели бы декорацией.
  clearCorpses();
  dropRemotes();
}

// ============================================================
//  Экраны оверлея
// ============================================================

const SCREENS = ['setup', 'lobbyScreen', 'waitScreen', 'goScreen', 'pauseScreen', 'overScreen'];

/** Показать один экран оверлея (или `null` — спрятать оверлей целиком). */
function showScreen(name) {
  for (const s of SCREENS) el(s)?.classList.toggle('hidden', s !== name);
  overlay.classList.toggle('hidden', name === null);
}

/**
 * Игрок представился (ник принят) — переходим из настроек в лобби.
 * Зовётся из `input.js`, сразу после `sendHello`.
 */
export function enterLobby() {
  showScreen('lobbyScreen');
  renderLobby();
}

/** Вкладка лобби. Тренировка входит сразу, дезматч показывает слоты. */
function setTab(next) {
  tab = next;
  for (const t of document.querySelectorAll('#lobbyTabs .tab')) {
    t.classList.toggle('on', t.dataset.tab === next);
  }
  renderLobby();
}

function renderLobby() {
  if (inRoom()) return;                 // мы уже в комнате — лобби не рисуем
  showScreen('lobbyScreen');
  const training = rooms.find((r) => r.mode === MODE_TRAINING);
  const matches = rooms.filter((r) => r.mode === MODE_DEATHMATCH);

  el('lobbyTraining').classList.toggle('hidden', tab !== 'training');
  el('lobbyDm').classList.toggle('hidden', tab !== 'dm');

  if (tab === 'training') {
    const n = training ? training.humans[0] + training.humans[1] : 0;
    const bots = training ? training.bots[0] + training.bots[1] : 0;
    el('trainInfo').textContent = training
      ? `Сейчас в песочнице: ${n} игрок(ов) и ${bots} ботов`
      : 'Тренировочная комната недоступна';
    el('trainGo').classList.toggle('off', !training);
    return;
  }

  // ---- Дезматч: комнаты со слотами ----
  const box = el('dmRooms');
  box.textContent = '';
  if (!matches.length) {
    box.textContent = 'Нет доступных матчей — сервер поднимает новую комнату…';
    return;
  }
  for (const r of matches) {
    box.appendChild(roomCard(r));
  }
}

/** Одна комната в списке: заголовок, состояние и два ряда слотов. */
function roomCard(r) {
  const card = document.createElement('div');
  card.className = 'dm-room';

  const head = document.createElement('div');
  head.className = 'dm-head';
  head.innerHTML = `<b>Матч #${r.id}</b> <span class="dm-state">${phaseText(r)}</span>`;
  card.appendChild(head);

  for (const team of [TEAM_T, TEAM_CT]) {
    const row = document.createElement('div');
    row.className = 'dm-team t' + team;
    const name = document.createElement('span');
    name.className = 'dm-name';
    name.textContent = TEAM_NAME[team];
    row.appendChild(name);

    // Слоты: сначала занятые людьми, потом свободные (их добьют боты).
    const taken = r.humans[team];
    for (let i = 0; i < TEAM_SLOTS; i++) {
      const slot = document.createElement('span');
      const free = i >= taken;
      slot.className = 'dm-slot' + (free ? ' free' : ' busy');
      slot.textContent = free ? '+' : '●';
      slot.title = free ? 'Свободно (займёт бот, если не занять)' : 'Занято игроком';
      if (free && open(r)) {
        slot.addEventListener('click', () => sendPick(r.id, team, S.difficulty | 0));
      } else {
        slot.classList.add('off');
      }
      row.appendChild(slot);
    }
    const hint = document.createElement('span');
    hint.className = 'dm-hint';
    hint.textContent = `люди ${taken} · боты добьют ${TEAM_SLOTS - taken}`;
    row.appendChild(hint);
    card.appendChild(row);
  }
  return card;
}

/** Открыта ли комната для входа — зеркало `lobby::RoomInfo::open`. */
const open = (r) => r.mode === MODE_TRAINING || r.phase === PHASE_WAITING || r.phase === PHASE_COUNTDOWN;

function phaseText(r) {
  const people = r.humans[0] + r.humans[1];
  switch (r.phase) {
    case PHASE_WAITING:
      return `ждём игроков (${people} из ${MATCH_MIN_HUMANS})`;
    case PHASE_COUNTDOWN:
      return `старт через ${r.secs} с`;
    case PHASE_LIVE:
      return `идёт бой, осталось ${mmss(r.secs)}`;
    case PHASE_OVER:
      return 'матч кончился, комната освобождается';
    default:
      return '';
  }
}

/** Экран ожидания: слот занят, боя ещё нет. */
function renderWait() {
  showScreen('waitScreen');
  const dm = M.mode === MODE_DEATHMATCH;
  el('waitTeam').textContent = dm && M.team !== TEAM_NONE
    ? `Ты в команде: ${TEAM_NAME[M.team]}`
    : 'Тренировка';
  const people = M.humansT + M.humansCt;
  if (M.phase === PHASE_COUNTDOWN) {
    el('waitTitle').textContent = `Матч начнётся через ${Math.ceil(M.left)}`;
    el('waitInfo').textContent =
      'Можно ещё позвать друзей: пока идёт отсчёт, слоты открыты. '
      + 'Если кто-то выйдет — отсчёт отменится.';
  } else {
    el('waitTitle').textContent = 'Ждём игроков';
    el('waitInfo').textContent =
      `Сейчас в матче ${people} из ${MATCH_MIN_HUMANS} нужных. `
      + 'Свободные слоты займут боты, когда бой начнётся.';
  }
  el('waitSlots').textContent =
    `Террористы: ${M.humansT}/${TEAM_SLOTS} · Контр-террористы: ${M.humansCt}/${TEAM_SLOTS}`;
}

/** Экран итогов: победившая команда, счёт и топ игрока. */
function renderOver() {
  showScreen('overScreen');
  if (overShown) return;
  overShown = true;
  const t = M.scoreT, ct = M.scoreCt;
  el('overTitle').textContent = t === ct
    ? 'Ничья'
    : `Победили ${TEAM_NAME[t > ct ? TEAM_T : TEAM_CT]}`;
  el('overScore').textContent = `${TEAM_SHORT[0]} ${t} : ${ct} ${TEAM_SHORT[1]}`;
  // Топ игрока — по убийствам, при равенстве выше тот, кто меньше умирал.
  const top = [...scores.entries()]
    .sort((a, b) => b[1].kills - a[1].kills || a[1].deaths - b[1].deaths)[0];
  el('overTop').textContent = top
    ? `Топ игрок: ${nameOf(top[0])} — ${top[1].kills} убийств, ${top[1].deaths} смертей`
    : '';
  el('overTable').innerHTML = scoreTable();
}

// ============================================================
//  HUD матча: таймер и счёт
// ============================================================

function updateMatchBar() {
  const bar = el('matchbar');
  if (!bar) return;
  const show = inGame && M.mode === MODE_DEATHMATCH;
  bar.classList.toggle('on', show);
  if (!show) return;
  const mine = M.team === TEAM_CT ? M.scoreCt : M.scoreT;
  const theirs = M.team === TEAM_CT ? M.scoreT : M.scoreCt;
  bar.innerHTML =
    `<span class="mb-us">${mine}</span>`
    + `<span class="mb-time">${mmss(Math.ceil(M.left))}</span>`
    + `<span class="mb-them">${theirs}</span>`;
}

/** Секунды → «м:сс» */
function mmss(s) {
  const v = Math.max(0, Math.round(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
}

/**
 * Локальный докрут времени между кадрами сервера.
 *
 * ⚠️ Не «свои часы»: значение приходит 5 Гц кадром `OP_MATCH` и здесь только
 * уменьшается до следующего кадра, чтобы секунды не прыгали через одну.
 * Расходиться этому нечем — любой пришедший кадр перезаписывает значение.
 */
setInterval(() => {
  if (!inRoom()) return;
  if (M.phase === PHASE_COUNTDOWN || M.phase === PHASE_LIVE || M.phase === PHASE_OVER) {
    M.left = Math.max(0, M.left - 0.2);
  }
  if (inGame) updateMatchBar();
  else if (M.phase === PHASE_COUNTDOWN || M.phase === PHASE_WAITING) renderWait();
}, 200);

// ============================================================
//  Таблица очков (Tab)
// ============================================================

const nameOf = (id) => (id === me.id ? 'Вы' : (nicks.get(id) || `#${id}`));

function scoreTable() {
  const rows = (team) => [...scores.entries()]
    .filter(([, s]) => (M.mode === MODE_DEATHMATCH ? s.team === team : true))
    .sort((a, b) => b[1].kills - a[1].kills || a[1].deaths - b[1].deaths)
    .map(([id, s]) => `<tr class="${id === me.id ? 'me' : ''}${s.alive ? '' : ' dead'}">`
      + `<td>${nameOf(id)}${s.npc ? ' <i>бот</i>' : ''}</td>`
      + `<td>${s.kills}</td><td>${s.deaths}</td></tr>`)
    .join('');

  const head = '<tr><th>Игрок</th><th>Убийств</th><th>Смертей</th></tr>';
  if (M.mode !== MODE_DEATHMATCH) {
    return `<table class="sb"><caption>Тренировка</caption>${head}${rows(TEAM_NONE)}</table>`;
  }
  const cap = (team) => `<caption class="t${team}">${TEAM_NAME[team]} — `
    + `${team === TEAM_CT ? M.scoreCt : M.scoreT}</caption>`;
  return `<table class="sb">${cap(TEAM_T)}${head}${rows(TEAM_T)}</table>`
    + `<table class="sb">${cap(TEAM_CT)}${head}${rows(TEAM_CT)}</table>`;
}

function renderScores() {
  const box = el('scoreboard');
  box.innerHTML = `<div class="sb-time">${mmss(Math.ceil(M.left))}</div>${scoreTable()}`;
}

// ---- Клавиша Tab ----
//
// ⚠️ `preventDefault` обязателен: Tab по умолчанию уводит фокус на элементы
// страницы, и после первого нажатия клавиши игры начали бы «нажиматься»
// на кнопку лобби вместо игрока.
addEventListener('keydown', (e) => {
  if (e.code !== 'Tab') return;
  e.preventDefault();
  if (!inGame || S.showScores) return;
  S.showScores = true;
  renderScores();
  el('scoreboard').classList.remove('hidden');
});
addEventListener('keyup', (e) => {
  if (e.code !== 'Tab') return;
  e.preventDefault();
  S.showScores = false;
  el('scoreboard').classList.add('hidden');
});

// ============================================================
//  Кнопки экранов
// ============================================================

for (const t of document.querySelectorAll('#lobbyTabs .tab')) {
  t.addEventListener('click', () => setTab(t.dataset.tab));
}
el('trainGo').addEventListener('click', () => sendPick(trainingRoom(), TEAM_NONE, S.difficulty | 0));
// Выход из слота/матча: сервер уберёт тело и пришлёт список комнат, по нему
// экран сам вернётся в лобби (см. приём `OP_LOBBY`).
el('waitLeave').addEventListener('click', () => sendQuit());
el('overLeave').addEventListener('click', () => sendQuit());
el('pauseLeave').addEventListener('click', () => sendQuit());
// Оба «в бой» — это ЖЕСТ пользователя, ради которого экраны и существуют
// (Pointer Lock без жеста браузер вправе не дать, см. `enterGame`).
el('goBattle').addEventListener('click', grabMouse);
el('pauseBack').addEventListener('click', grabMouse);

/** Номер тренировочной комнаты из списка (а не число в коде: комнату
 *  создаёт сервер, и её номер — его дело). */
function trainingRoom() {
  const r = rooms.find((x) => x.mode === MODE_TRAINING);
  return r ? r.id : 1;
}
